import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Hammer, Check, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { fmtEur } from "@/components/ui-kit";
import { useAuth } from "@/contexts/AuthContext";
import { getNegozioFilter, NEGOZIO_FILTER_EVENT } from "@/components/AppLayout";

const Page = ({ children }) => <div className="p-6 max-w-7xl mx-auto space-y-6">{children}</div>;

const PKG_NAMES = { "pkg-basic": "BASIC", "pkg-smart": "SMART", "pkg-premium": "PREMIUM", "pkg-elite": "ELITE" };

const STATI_OPTIONS = [
  { k: "da_iniziare", lbl: "🕐 Da iniziare", cls: "bg-zinc-50 text-zinc-800 border-zinc-300" },
  { k: "in_corso", lbl: "⚙️ In corso", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  { k: "sospesa", lbl: "⏸ Sospesa", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  { k: "completata", lbl: "✅ Completata", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  { k: "annullata", lbl: "❌ Annullata", cls: "bg-rose-50 text-rose-800 border-rose-300" },
];

export default function GestoreCantieri() {
  const [cantieri, setCantieri] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stato, setStato] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const reload = () => {
    setLoading(true);
    const nid = getNegozioFilter();
    const url = nid ? `/gestore/cantieri?negozio_id=${encodeURIComponent(nid)}` : "/gestore/cantieri";
    return api.get(url)
      .then(r => setCantieri(r.data || []))
      .catch(() => {})
      .finally(() => { setLoading(false); setSelectedIds(new Set()); });
  };
  useEffect(() => {
    reload();
    const handler = () => reload();
    window.addEventListener(NEGOZIO_FILTER_EVENT, handler);
    return () => window.removeEventListener(NEGOZIO_FILTER_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convalida = async (assId, avId) => {
    try {
      const r = await api.post(`/subappaltatori/assegnazioni/${assId}/avanzamenti/${avId}/convalida`);
      const importo = r?.data?.pagamento_importo || 0;
      const movId = r?.data?.pagamento_movimento_id;
      if (movId && importo > 0) {
        toast.success(`SAL convalidato · Pagamento programmato di € ${importo.toLocaleString("it-IT", { minimumFractionDigits: 2 })} aggiunto alla Cassa Commessa`);
      } else if (r?.data?.already_validated) {
        toast.info("SAL già convalidato in precedenza.");
      } else {
        toast.success("Avanzamento convalidato. Pagamento sbloccato.");
      }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const cambiaStato = async (c, nuovo) => {
    if (c.stato === nuovo) return;
    if (!window.confirm(`Cambiare stato cantiere ${c.numero || c.id} a "${nuovo}"?`)) return;
    try { await api.patch(`/commesse/${c.id}/stato`, { stato: nuovo }); toast.success(`Stato → ${nuovo}`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore cambio stato"); }
  };
  const delOne = async (c) => {
    if (!window.confirm(`Eliminare il cantiere ${c.numero || c.id}? L'operazione è irreversibile.`)) return;
    try { await api.delete(`/commesse/${c.id}`); toast.success("Cantiere eliminato"); reload(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Errore eliminazione"); }
  };
  const toggleId = (id) => setSelectedIds((prev) => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleAll = () => {
    const ids = filt.map((c) => c.id);
    const all = ids.every((i) => selectedIds.has(i)) && ids.length > 0;
    setSelectedIds(all ? new Set() : new Set(ids));
  };
  const delSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Eliminare ${ids.length} cantieri selezionati? Le collezioni collegate (allegato A, foto cantiere, SAL) verranno anch'esse cancellate.`)) return;
    try { const { data } = await api.post("/commesse/bulk-delete", { ids }); toast.success(`Eliminati ${data.deleted} cantieri`); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore bulk delete"); }
  };

  const filt = cantieri.filter((c) => !stato || c.stato === stato);
  const totali = cantieri.length;
  const in_corso = cantieri.filter((r) => r.stato === "in_corso").length;
  const fatturato = cantieri.reduce((s, r) => s + (r.fatturato || 0), 0);
  const incassato = cantieri.reduce((s, r) => s + (r.incassato || 0), 0);
  const da_convalidare = cantieri.reduce((s, r) => s + (r.avanzamenti_da_convalidare || 0), 0);

  return (
    <Page>
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Gestione Cantieri</h1>
        <p className="text-sm text-zinc-500 mt-1">Tutti i cantieri attivi e da iniziare. Convalida gli avanzamenti dei subappaltatori per sbloccare i pagamenti.</p>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900 leading-relaxed" data-testid="sal-auto-pay-banner">
        <strong>Auto-pagamento SAL attivo</strong> — ogni volta che convalidi un avanzamento, il sistema crea automaticamente un movimento <em>uscita programmata</em> nella <strong>Cassa Commessa</strong> con scadenza a 15 giorni, beneficiario subappaltatore, importo = <code>importo_pattuito × Δ% SAL</code>. Trovi i pagamenti nella tab <strong>Cassa & Pagamenti</strong> del Workflow Commessa.
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI label="Cantieri" value={totali} testid="kpi-totali" />
        <KPI label="In corso" value={in_corso} color="text-blue-600" testid="kpi-in-corso" />
        <KPI label="Fatturato" value={fmtEur(fatturato)} testid="kpi-fatturato" />
        <KPI label="Incassato" value={fmtEur(incassato)} color="text-emerald-600" testid="kpi-incassato" />
        <KPI label="Da convalidare" value={da_convalidare} color="text-amber-600" testid="kpi-pending" />
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {[["","Tutti"],["da_iniziare","Da Iniziare"],["in_corso","In Corso"],["completata","Completate"],["sospesa","Sospese"],["annullata","Annullate"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setStato(k)} data-testid={`gc-filter-${k || "all"}`}
            className={`px-3 py-1.5 rounded-full text-sm border ${stato === k ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-100"}`}>{lbl}</button>
        ))}
        {isAdmin && (
          <label className="ml-auto flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
            <input type="checkbox" className="h-4 w-4 cursor-pointer" checked={filt.length > 0 && filt.every((c) => selectedIds.has(c.id))} onChange={toggleAll} data-testid="gc-select-all" />
            Seleziona tutti visibili
          </label>
        )}
        {isAdmin && selectedIds.size > 0 && (
          <Button size="sm" onClick={delSelected} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="gc-bulk-delete">
            <Trash2 className="h-4 w-4 mr-1" /> Elimina selezionati ({selectedIds.size})
          </Button>
        )}
      </div>

      {loading ? <div className="text-zinc-400 mono">caricamento…</div>
        : filt.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded p-6 text-zinc-500">Nessun cantiere.</div>
        ) : (
          <div className="space-y-4">
            {filt.map(c => {
              const pending = (c.assegnazioni || []).flatMap(a => (a.avanzamenti || []).filter(av => !av.convalidato).map(av => ({ a, av })));
              const stKey = c.stato || "da_iniziare";
              const stCls = (STATI_OPTIONS.find((s) => s.k === stKey) || STATI_OPTIONS[0]).cls;
              const selected = selectedIds.has(c.id);
              return (
                <div key={c.id} className={`bg-white border rounded p-4 space-y-3 ${selected ? "border-blue-500 ring-2 ring-blue-200" : "border-zinc-200"}`} data-testid={`cantiere-card-${c.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {isAdmin && (
                          <input type="checkbox" className="h-4 w-4 cursor-pointer" checked={selected} onChange={() => toggleId(c.id)} data-testid={`gc-select-${c.id}`} />
                        )}
                        <span className="font-mono text-xs text-zinc-500">{c.numero}</span>
                        <select
                          value={stKey}
                          onChange={(e) => cambiaStato(c, e.target.value)}
                          className={`text-xs rounded border cursor-pointer px-2 py-0.5 font-semibold ${stCls}`}
                          data-testid={`gc-stato-${c.id}`}
                          disabled={!isAdmin && (user?.role || "").toLowerCase() !== "gestore"}
                        >
                          {STATI_OPTIONS.map((s) => <option key={s.k} value={s.k}>{s.lbl}</option>)}
                        </select>
                        {c.package_id && <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 bg-zinc-100 rounded" style={{ color: "var(--brand)" }}>{PKG_NAMES[c.package_id] || ""}</span>}
                      </div>
                      <div className="font-semibold text-lg mt-1">{c.cliente?.nome || ""} {c.cliente?.cognome || ""}</div>
                      <div className="text-xs text-zinc-500 mt-0.5">Avanzamento: {c.avanzamento_pct || 0}% · MQ: {c.mq || 0} · Preventivo: {fmtEur(c.totale_preventivo)} · Fatturato: {fmtEur(c.fatturato)}</div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {c.avanzamenti_da_convalidare > 0 && (
                        <span className="bg-amber-100 text-amber-800 px-2 py-1 text-xs mono rounded" data-testid={`pending-${c.id}`}>
                          {c.avanzamenti_da_convalidare} da convalidare
                        </span>
                      )}
                      <Link to={`/dettagliocommessa/${c.id}`} className="text-blue-600 text-xs hover:underline px-3 py-1.5 border border-blue-200 rounded hover:bg-blue-50" data-testid={`detail-comm-${c.id}`}><Eye size={14} className="inline mr-1" />Dettaglio</Link>
                      {isAdmin && (
                        <Button size="sm" variant="outline" onClick={() => delOne(c)} className="text-rose-600 hover:bg-rose-50 border-rose-200 h-7" data-testid={`gc-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="h-1.5 bg-zinc-100 rounded overflow-hidden"><div className="h-full" style={{ width: `${c.avanzamento_pct || 0}%`, background: "var(--brand)" }} /></div>
                  {pending.length > 0 && (
                    <div className="border-t pt-3 space-y-2">
                      <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Avanzamenti in attesa di convalida</div>
                      {pending.map(({ a, av }) => (
                        <div key={av.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 p-2 rounded text-xs gap-2" data-testid={`pending-av-${av.id}`}>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{av.descrizione}</span> · {av.percentuale}% · sub#{a.subappaltatore_id?.slice(-6)}
                            <div className="text-[10px] text-zinc-500 mono">{av.dichiarato_il?.slice(0, 16).replace("T", " ")}</div>
                          </div>
                          <Button size="sm" className="rounded-sm h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => convalida(a.id, av.id)} data-testid={`gestore-convalida-${av.id}`}>
                            <Check size={12} className="mr-1" /> Convalida & sblocca pagamento
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </Page>
  );
}

function KPI({ label, value, color = "text-zinc-900", testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
